import { useEffect, useRef, useState } from 'react';
import { autoUpdate, flip, offset, shift, useFloating, useClick, useDismiss, useRole, useInteractions, useHover, useFocus, arrow, useTransitionStyles, safePolygon, FloatingPortal } from '@floating-ui/react';
import { PopoverContainer } from './PopoverContainer';
import { PopoverHeader } from './PopoverHeader';
import { PopoverContent } from './PopoverContent';
import { useInterstateTheme } from '../../InterstateThemeProvider';
import { PopoverArrow } from './PopoverArrow';
import { clsx } from 'clsx';
import { idBuilder } from '../../internal';
import { mergeSxStyle } from '../../common/mergeSxStyle';
import { Box } from '../../Box';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const TRANSITION_DURATION = 500;
export const Popover = props => {
  const {
    popoverContent,
    children,
    padding = true,
    position = 'top',
    variant = 'withPointer',
    header,
    className,
    id,
    onOpen,
    open,
    trigger = 'outsideClick',
    ...rest
  } = props;
  const theme = useInterstateTheme();
  const [_isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (open === undefined) {
      return;
    }
    setIsOpen(open);
  }, [open]);
  const arrowRef = useRef(null);
  const handleOpen = openState => {
    if (onOpen) {
      onOpen(openState);
    }
    setIsOpen(openState);
  };
  const {
    refs,
    floatingStyles,
    context
  } = useFloating({
    open: _isOpen,
    onOpenChange: handleOpen,
    middleware: [offset(variant === 'pointerless' ? 4 : 13), flip(), shift(), arrow({
      element: arrowRef
    })],
    whileElementsMounted: autoUpdate,
    placement: position
  });
  const click = useClick(context, {
    enabled: trigger === 'click' || trigger === 'outsideClick'
  });
  const dismiss = useDismiss(context, {
    outsidePress: trigger === 'outsideClick',
    escapeKey: true,
    capture: true
  });
  const role = useRole(context);
  const hover = useHover(context, {
    handleClose: safePolygon({
      requireIntent: false
    }),
    enabled: trigger === 'hover'
  });
  const focus = useFocus(context, {
    enabled: trigger === 'focus'
  });
  // Merge all the interactions into prop getters
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([dismiss, role, click, hover, focus]);
  const {
    isMounted,
    styles
  } = useTransitionStyles(context, {
    duration: TRANSITION_DURATION
  });
  const mergedFloatingStyle = mergeSxStyle(floatingStyles, {
    zIndex: 1300
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Box, {
      width: 'fit-content',
      ref: refs.setReference,
      ...getReferenceProps(),
      className: 'ids-popover-anchor-container',
      id: idBuilder('ids-popover-anchor-container', id),
      "data-testid": idBuilder('ids-popover-anchor-container', rest['data-testid']),
      children: children
    }), isMounted && /*#__PURE__*/_jsx(FloatingPortal, {
      id: idBuilder('ids-popover-root', id),
      children: /*#__PURE__*/_jsx(Box, {
        ref: refs.setFloating,
        sx: mergedFloatingStyle,
        ...getFloatingProps(),
        className: clsx([className, 'ids-popover-floating-container']),
        id: idBuilder('ids-popover-floating-container', id),
        "data-testid": idBuilder('ids-popover-floating-container', rest['data-testid']),
        children: /*#__PURE__*/_jsxs(Box, {
          sx: styles,
          className: 'ids-popover-transition-container',
          children: [/*#__PURE__*/_jsxs(PopoverContainer, {
            id: id,
            "data-testid": rest['data-testid'],
            children: [/*#__PURE__*/_jsx(PopoverHeader, {
              header: header,
              onClickClose: () => handleOpen(false),
              id: id,
              "data-testid": rest['data-testid']
            }), /*#__PURE__*/_jsx(PopoverContent, {
              usePadding: Boolean(padding),
              id: id,
              "data-testid": rest['data-testid'],
              hasHeader: Boolean(header),
              children: popoverContent
            })]
          }), /*#__PURE__*/_jsx(PopoverArrow, {
            theme: theme,
            variant: variant,
            ref: arrowRef,
            context: context
          })]
        })
      })
    })]
  });
};